// One hour-by-hour day plan (handoff v08 #23, «Η μέρα μου» / Agenda → Day). The plan of a day is read from what the CRM
// already holds plus the advisor's own blocks (schema 14 `plan_blocks`):
//
//   appointment  tasks of kind «appointment» with a time — COMMITTED: never moved, resized or cancelled from here (they
//                open in their own form); planner commands refuse to change them
//   meeting      a planned meeting record of that day with no appointment task behind it — committed as well
//   task         any other task with a time on that day (a call at 11:00): movable here (its time changes, nothing else)
//   block        the advisor's own block: focus time, personal programme, a planned task (taskId), an accepted goal
//                suggestion (source «suggestion», kind «goal»), admin, travel, break
//
// Overlaps are SHOWN (conflictWith), never resolved automatically. A block placed over a committed appointment is refused
// unless the advisor confirms it (`allowConflict`) — and even then the appointment itself is untouched.
// Free slots = the working hours (Settings → office) minus everything planned. The printed «Ημερήσιο φύλλο» reads the
// same `plannerItems`, so paper and screen agree.
import type { Changeset, Put, WorkspaceView } from './changeset.ts';
import { DomainError, clean, created, opt, put, timeline, touched, type Ctx } from './cmd.ts';
import { addDays, isDay, normalizeTime, toDay, weekday, type Day } from './dates.ts';
import { newId } from './ids.ts';
import { completeTask, getSetting, saveSetting } from './services.ts';
import { getOffice } from './setup.ts';
import { PLAN_ACTIVITIES, PLAN_BLOCK_KINDS, type PlanActivity, type PlanBlock, type PlanBlockKind, type PlanBlockSource, type Task } from './schema.ts';

// --- time helpers ------------------------------------------------------------------------------------------------
/** 'HH:MM' → minutes after midnight (NaN when malformed) */
export function toMin(t: string | null | undefined): number {
  const m = /^(\d{2}):(\d{2})$/.exec(String(t ?? ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
}
/** minutes after midnight → 'HH:MM' (kept inside the day: 00:00 … 23:59) */
export function fromMin(n: number): string {
  const x = Math.max(0, Math.min(23 * 60 + 59, Math.round(n)));
  return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`;
}
/** the advisor's local calendar day of an ISO timestamp (a timestamp without a zone is local already) */
export function localDayOf(iso: string | null | undefined): Day | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? (isDay(String(iso).slice(0, 10)) ? String(iso).slice(0, 10) : null) : toDay(d);
}
/** the local HH:MM of an ISO timestamp */
export function localTimeOf(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
export interface Interval { start: number; end: number }
/** the union of intervals (sorted, merged) */
export function mergeIntervals(xs: readonly Interval[]): Interval[] {
  const s = xs.filter((x) => x.end > x.start).map((x) => ({ ...x })).sort((a, b) => a.start - b.start);
  const out: Interval[] = [];
  for (const x of s) { const last = out[out.length - 1]; if (last && x.start <= last.end) last.end = Math.max(last.end, x.end); else out.push(x); }
  return out;
}
/** minutes of `xs` that fall inside [from, to) (overlaps counted once) */
export function minutesWithin(xs: readonly Interval[], from: number, to: number): number {
  return mergeIntervals(xs.map((x) => ({ start: Math.max(from, x.start), end: Math.min(to, x.end) }))).reduce((s, x) => s + (x.end - x.start), 0);
}
/** [from, to) minus the intervals */
export function subtractIntervals(from: number, to: number, busy: readonly Interval[]): Interval[] {
  const out: Interval[] = [];
  let cur = from;
  for (const b of mergeIntervals(busy)) {
    if (b.end <= cur) continue;
    if (b.start >= to) break;
    if (b.start > cur) out.push({ start: cur, end: Math.min(b.start, to) });
    cur = Math.max(cur, b.end);
    if (cur >= to) break;
  }
  if (cur < to) out.push({ start: cur, end: to });
  return out;
}
const overlaps = (a: Interval, b: Interval) => a.start < b.end && b.start < a.end;

// --- settings ----------------------------------------------------------------------------------------------------
export interface PlannerSettings { from: string; to: string; slotMin: 15 | 30 }
export const PLANNER_KEY = 'planner';
export const DEFAULT_PLANNER: PlannerSettings = { from: '08:00', to: '20:00', slotMin: 30 };
/** the effective grid: hours shown (whole hours) and the slot size; malformed values fall back to the default */
export function normalizePlanner(raw: unknown): PlannerSettings {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const hour = (x: unknown, d: string) => { const t = normalizeTime(x); return t ? `${t.slice(0, 2)}:00` : d; };
  let from = hour(o.from, DEFAULT_PLANNER.from), to = hour(o.to, DEFAULT_PLANNER.to);
  if (toMin(to) - toMin(from) < 120) { from = DEFAULT_PLANNER.from; to = DEFAULT_PLANNER.to; }
  return { from, to, slotMin: o.slotMin === 15 ? 15 : 30 };
}
export const plannerSettings = (v: WorkspaceView): PlannerSettings => normalizePlanner(getSetting<unknown>(v, PLANNER_KEY, null));

/** the working window of a day from Settings → office (null on a day the advisor does not work) */
export function workingWindow(v: WorkspaceView, day: Day): Interval | null {
  const o = getOffice(v);
  const iso = weekday(day) || 7;
  if (!o.days.includes(iso)) return null;
  const start = toMin(o.from), end = toMin(o.to);
  return Number.isFinite(start) && Number.isFinite(end) && end > start ? { start, end } : null;
}

// --- block types (v17, owner 22 Oct 2026) ----------------------------------------------------------------------
// What the advisor does in a block. «client_appointment» is the insurance appointment: it is never a block — choosing it
// creates a real CRM appointment (tasks.kind «appointment»), so the appointment KPIs stay exact. «coffee» and
// «referral_meeting» are relationship meetings: counted on their own (relationshipCounts), never as appointments.
// `kind` stays one of the schema-16 values (what an app of schema 16 shows); the legacy types (focus, task, goal and
// the plain kinds of blocks made before 17) keep their own label.
export type PlanType = PlanActivity | 'client_appointment' | 'focus' | 'task' | 'goal';
/** the phone calendars (one subscribed calendar per category, see calendar-feed.ts) */
export type FeedCategory = 'appointments' | 'plan' | 'calls' | 'personal';
/** the types offered when adding, in this order (client appointment first: it opens the appointment form) */
export const PLAN_TYPE_CHOICES: readonly PlanType[] = ['client_appointment', ...PLAN_ACTIVITIES];
export const ACTIVITY_INFO: Record<PlanActivity, { kind: PlanBlockKind; minutes: number }> = {
  coffee: { kind: 'focus', minutes: 60 }, referral_meeting: { kind: 'focus', minutes: 60 }, calls: { kind: 'focus', minutes: 60 },
  followup: { kind: 'focus', minutes: 30 }, admin: { kind: 'admin', minutes: 30 }, prep: { kind: 'focus', minutes: 30 },
  learning: { kind: 'focus', minutes: 60 }, team: { kind: 'focus', minutes: 60 }, travel: { kind: 'travel', minutes: 30 },
  break: { kind: 'break', minutes: 15 }, personal: { kind: 'personal', minutes: 60 }, other: { kind: 'focus', minutes: 30 },
};
/** relationship meetings (no sale): counted separately from insurance appointments everywhere */
export const SOCIAL_ACTIVITIES: ReadonlySet<PlanActivity> = new Set<PlanActivity>(['coffee', 'referral_meeting']);
export const isSocialActivity = (a: string | null | undefined): boolean => !!a && SOCIAL_ACTIVITIES.has(a as PlanActivity);
export const isPlanActivity = (a: unknown): a is PlanActivity => typeof a === 'string' && (PLAN_ACTIVITIES as readonly string[]).includes(a);
const GOAL_ACTIVITY: Record<string, PlanActivity> = { prospecting_calls: 'calls', overdue_followups: 'followup', renewals: 'followup', customer_contacts: 'followup' };
const TASK_ACTIVITY: Record<string, PlanType> = { call: 'calls', followup: 'followup', renewal: 'followup', review: 'prep', document: 'admin' };
/** the type of a plan item (colour, icon, label, phone calendar) */
export function planTypeOf(x: { source: string; kind: string; activity?: string | null; goalType?: string | null }): PlanType {
  if (x.source === 'appointment' || x.source === 'meeting') return 'client_appointment';
  if (isPlanActivity(x.activity)) return x.activity;
  if (x.source === 'task') return TASK_ACTIVITY[x.kind] ?? 'task';
  if (x.kind === 'goal') return GOAL_ACTIVITY[x.goalType ?? ''] ?? 'goal';
  if (x.kind === 'personal' || x.kind === 'admin' || x.kind === 'travel' || x.kind === 'break') return x.kind;
  return x.kind === 'task' ? 'task' : 'focus';
}
/** which phone calendar a type belongs to */
export function feedCategoryOf(type: PlanType): FeedCategory {
  if (type === 'client_appointment') return 'appointments';
  if (type === 'calls' || type === 'followup') return 'calls';
  if (type === 'break' || type === 'personal') return 'personal';
  return 'plan';
}
/** the activity of a block planned for a task (a call task → «calls»), or null */
export const activityForTask = (t: Pick<Task, 'kind'>): PlanActivity | null => { const a = TASK_ACTIVITY[t.kind]; return a && isPlanActivity(a) ? a : null; };

// --- the items of a day ------------------------------------------------------------------------------------------
export type PlanItemSource = 'appointment' | 'meeting' | 'task' | 'block' | 'suggestion';
export interface PlanItem {
  /** stable key: task:<id> · meeting:<id> · block:<id> · sug:<goal type> */
  key: string;
  source: PlanItemSource;
  kind: string;
  start: string; end: string;
  title: string;
  status: 'open' | 'planned' | 'done' | 'cancelled';
  personId?: string | null; prospectId?: string | null; taskId?: string | null; blockId?: string | null; meetingId?: string | null;
  goalType?: string | null;
  /** v17: the block's activity (null for appointments, tasks and blocks made before 17) and the derived type */
  activity?: PlanActivity | null;
  type: PlanType;
  /** the advisor's notes (block notes, task notes) — printed on the advisor's own sheet, never sent to a phone calendar */
  notes?: string | null;
  /** committed appointments and meetings are never changed from the planner */
  committed: boolean;
  /** can be moved / resized here */
  movable: boolean;
  /** keys of the items it overlaps (shown, never resolved automatically) */
  conflictWith: string[];
}
const DEFAULT_TASK_MIN = 30;
const interval = (x: Pick<PlanItem, 'start' | 'end'>): Interval => ({ start: toMin(x.start), end: toMin(x.end) });
const taskEnd = (t: Task, fallback: number) => fromMin(toMin(t.dueTime!) + Math.max(5, Math.min(12 * 60, Number(t.durationMin) || fallback)));
/** the end time of a timed task (appointments last the office meeting length unless they say otherwise) */
export function taskEndTime(v: WorkspaceView, t: Task): string { return taskEnd(t, t.kind === 'appointment' ? getOffice(v).meetingMinutes : DEFAULT_TASK_MIN); }

/** everything planned on `day` (no suggestions), by start time, with the overlaps marked */
export function plannerItems(v: WorkspaceView, day: Day): PlanItem[] {
  const office = getOffice(v);
  const items: PlanItem[] = [];
  const apptTaskIds = new Set<string>();
  for (const t of v.rows('tasks')) {
    if (t.dueDate !== day || !t.dueTime || !Number.isFinite(toMin(t.dueTime)) || t.status === 'cancelled') continue;
    const appt = t.kind === 'appointment';
    if (appt) apptTaskIds.add(t.id);
    const source = appt ? 'appointment' : 'task';
    items.push({
      key: `task:${t.id}`, source, kind: t.kind, start: t.dueTime.slice(0, 5), end: taskEnd(t, appt ? office.meetingMinutes : DEFAULT_TASK_MIN),
      title: t.title, status: t.status === 'done' ? 'done' : 'open', personId: t.personId ?? null, prospectId: t.prospectId ?? null, taskId: t.id,
      type: planTypeOf({ source, kind: t.kind }), notes: t.notes ?? null,
      committed: appt, movable: !appt && t.status === 'open', conflictWith: [],
    });
  }
  for (const m of v.rows('meetings')) {
    if (m.status !== 'planned' || localDayOf(m.at) !== day || (m.taskId && apptTaskIds.has(m.taskId))) continue;
    if (m.taskId && v.get('tasks', m.taskId)?.status === 'cancelled') continue;
    const start = localTimeOf(m.at);
    if (!start) continue;
    items.push({ key: `meeting:${m.id}`, source: 'meeting', kind: 'appointment', start, end: fromMin(toMin(start) + office.meetingMinutes), title: m.purpose || '', status: 'open', personId: m.personId, meetingId: m.id, type: 'client_appointment', notes: m.agenda ?? null, committed: true, movable: false, conflictWith: [] });
  }
  for (const b of v.where('plan_blocks', 'day', day)) {
    if (b.status === 'cancelled') continue;
    items.push({
      key: `block:${b.id}`, source: 'block', kind: b.kind, start: b.start, end: b.end, title: b.title, status: b.status, taskId: b.taskId ?? null, blockId: b.id, goalType: b.goalType ?? null,
      personId: b.personId ?? (b.taskId ? v.get('tasks', b.taskId)?.personId ?? null : null), activity: b.activity ?? null, type: planTypeOf({ source: 'block', kind: b.kind, activity: b.activity, goalType: b.goalType }), notes: b.notes ?? null,
      committed: false, movable: b.status === 'planned', conflictWith: [],
    });
  }
  items.sort((a, b) => a.start.localeCompare(b.start) || a.end.localeCompare(b.end) || a.key.localeCompare(b.key));
  for (let i = 0; i < items.length; i++) for (let j = i + 1; j < items.length; j++) {
    if (overlaps(interval(items[i]!), interval(items[j]!))) { items[i]!.conflictWith.push(items[j]!.key); items[j]!.conflictWith.push(items[i]!.key); }
  }
  return items;
}

/** the busy intervals of a day; goal suggestion blocks are left out when `exceptGoals` (they ARE the goals' own time) */
export function busyIntervals(items: readonly PlanItem[], opts: { exceptGoals?: boolean } = {}): Interval[] {
  return items.filter((x) => x.status !== 'cancelled' && x.source !== 'suggestion' && !(opts.exceptGoals && x.kind === 'goal')).map(interval);
}

/** free time of a day inside the working hours (or the grid on a day off), from `notBefore` (minutes, e.g. now) */
export function freeSlots(v: WorkspaceView, day: Day, items = plannerItems(v, day), opts: { notBefore?: number; minMinutes?: number } = {}): Interval[] {
  const w = workingWindow(v, day);
  const g = plannerSettings(v);
  const from = Math.max(w ? w.start : toMin(g.from), opts.notBefore ?? 0);
  const to = w ? w.end : toMin(g.to);
  if (to <= from) return [];
  return subtractIntervals(from, to, busyIntervals(items)).filter((x) => x.end - x.start >= (opts.minMinutes ?? 15));
}

// --- commands ----------------------------------------------------------------------------------------------------
export interface PlanBlockInput {
  /** `kind` may be left out when an `activity` says it (v17) */
  id?: string; day: Day; start: string; end: string; kind?: PlanBlockKind; title?: string | null;
  taskId?: string | null; goalType?: string | null; notes?: string | null; source?: PlanBlockSource;
  /** v17: the type of the block; undefined keeps the block's current one */
  activity?: PlanActivity | null;
  /** v17: the person of a relationship meeting; undefined keeps the current one */
  personId?: string | null;
}
export interface PlanOpts { allowConflict?: boolean }
/** the committed appointments / meetings a time range of `day` would overlap (the item itself left out) */
export function committedOverlaps(v: WorkspaceView, day: Day, start: string, end: string, exceptKey?: string): PlanItem[] {
  const r = { start: toMin(start), end: toMin(end) };
  return plannerItems(v, day).filter((x) => x.committed && x.key !== exceptKey && overlaps(r, interval(x)));
}
function checkRange(start: string | null, end: string | null): [string, string] {
  if (!start || !end) throw new DomainError('plan.time');
  const a = toMin(start), b = toMin(end);
  if (!(b > a)) throw new DomainError('plan.end_before_start');
  if (b - a < 5) throw new DomainError('plan.too_short');
  if (b - a > 12 * 60) throw new DomainError('plan.too_long');
  return [start, end];
}
function guardCommitted(v: WorkspaceView, day: Day, start: string, end: string, exceptKey: string | undefined, opts: PlanOpts) {
  if (opts.allowConflict) return;
  const hit = committedOverlaps(v, day, start, end, exceptKey);
  if (hit.length) throw Object.assign(new DomainError('plan.overlaps_appointment'), { items: hit.map((x) => x.key) });
}

/** add or change one of the advisor's own blocks (never an appointment) */
export function savePlanBlock(v: WorkspaceView, input: PlanBlockInput, ctx: Ctx, opts: PlanOpts = {}): Changeset {
  const cur = input.id ? v.get('plan_blocks', input.id) : undefined;
  if (!isDay(input.day)) throw new DomainError('plan.day');
  const activity = input.activity !== undefined ? input.activity ?? null : cur?.activity ?? null;
  if (activity !== null && !isPlanActivity(activity)) throw new DomainError('plan.kind');
  // the kind follows the activity (what an app of schema 16 shows), except a task's or a goal's own block
  const asked = input.kind ?? cur?.kind;
  const kind: PlanBlockKind | undefined = asked === 'task' || asked === 'goal' || !activity ? asked : ACTIVITY_INFO[activity].kind;
  if (!kind || !(PLAN_BLOCK_KINDS as readonly string[]).includes(kind)) throw new DomainError('plan.kind');
  const personId = input.personId !== undefined ? opt(input.personId) : cur?.personId ?? null;
  if (personId && !v.get('persons', personId)) throw new DomainError('person.not_found');
  const [start, end] = checkRange(normalizeTime(input.start), normalizeTime(input.end));
  const task = input.taskId ? v.get('tasks', input.taskId) : undefined;
  if (input.taskId && !task) throw new DomainError('task.not_found');
  if (task?.kind === 'appointment') throw new DomainError('plan.appointment_fixed');
  const title = clean(input.title) || task?.title || '';
  if (!title) throw new DomainError('plan.title_required');
  guardCommitted(v, input.day, start, end, cur ? `block:${cur.id}` : undefined, opts);
  const fields = { day: input.day, start, end, kind, title: title.slice(0, 200), taskId: task?.id ?? null, goalType: opt(input.goalType), notes: opt(input.notes)?.slice(0, 2000) ?? null, activity, personId };
  const row: PlanBlock = cur
    ? touched(ctx, cur, { ...fields, status: 'planned', completedAt: null })
    : created<'plan_blocks'>(ctx, { id: input.id, ...fields, status: 'planned', source: input.source ?? 'advisor', completedAt: null });
  return { action: cur ? 'plan.block_update' : 'plan.block_add', entity: 'plan_blocks', entityId: row.id, summary: row.title, puts: [put('plan_blocks', row)], deletes: [] };
}
function openBlock(v: WorkspaceView, id: string): PlanBlock {
  const b = v.get('plan_blocks', id);
  if (!b) throw new DomainError('plan.not_found');
  if (b.status !== 'planned') throw new DomainError('plan.block_closed');
  return b;
}
/** move a block (same length) to another start, optionally another day */
export function movePlanBlock(v: WorkspaceView, id: string, to: { start: string; day?: Day }, ctx: Ctx, opts: PlanOpts = {}): Changeset {
  const b = openBlock(v, id);
  const len = toMin(b.end) - toMin(b.start);
  const s = toMin(normalizeTime(to.start) ?? '');
  if (!Number.isFinite(s)) throw new DomainError('plan.time');
  if (s + len > 24 * 60 - 1) throw new DomainError('plan.after_midnight');
  const day = to.day ?? b.day;
  if (!isDay(day)) throw new DomainError('plan.day');
  const start = fromMin(s), end = fromMin(s + len);
  guardCommitted(v, day, start, end, `block:${b.id}`, opts);
  return { action: 'plan.block_move', entity: 'plan_blocks', entityId: id, summary: b.title, puts: [put('plan_blocks', touched(ctx, b, { day, start, end }))], deletes: [] };
}
/** change the end of a block (the start stays) */
export function resizePlanBlock(v: WorkspaceView, id: string, end: string, ctx: Ctx, opts: PlanOpts = {}): Changeset {
  const b = openBlock(v, id);
  const [, e] = checkRange(b.start, normalizeTime(end));
  guardCommitted(v, b.day, b.start, e, `block:${b.id}`, opts);
  return { action: 'plan.block_resize', entity: 'plan_blocks', entityId: id, summary: b.title, puts: [put('plan_blocks', touched(ctx, b, { end: e }))], deletes: [] };
}
/** cancel a block (kept, hidden from the plan); a planned task stays open with its own date */
export function cancelPlanBlock(v: WorkspaceView, id: string, ctx: Ctx): Changeset {
  const b = openBlock(v, id);
  return { action: 'plan.block_cancel', entity: 'plan_blocks', entityId: id, summary: b.title, puts: [put('plan_blocks', touched(ctx, b, { status: 'cancelled', completedAt: ctx.now }))], deletes: [] };
}
/** done; a block for a task completes that task too (once — a task already done is left as it is). A relationship
 *  meeting (coffee, referral) with a person goes on that person's timeline as a meeting — their «last contact» — and
 *  is NOT an appointment (nothing in tasks, so the appointment KPIs do not change). */
export function completePlanBlock(v: WorkspaceView, id: string, ctx: Ctx): Changeset {
  const b = openBlock(v, id);
  const puts: Put[] = [put('plan_blocks', touched(ctx, b, { status: 'done', completedAt: ctx.now }))];
  const t = b.taskId ? v.get('tasks', b.taskId) : undefined;
  if (t && t.status === 'open') puts.push(...completeTask(v, t.id, {}, ctx).puts);
  if (b.personId && isSocialActivity(b.activity) && v.get('persons', b.personId)) puts.push(timeline(ctx, b.personId, 'meeting', b.title, b.notes ?? null, { table: 'plan_blocks', id: b.id }));
  return { action: 'plan.block_done', entity: 'plan_blocks', entityId: id, summary: b.title, puts, deletes: [] };
}
/** plan an untimed task into the day (a «task» block; the task keeps its own due date) */
export function planTask(v: WorkspaceView, taskId: string, at: { day: Day; start: string; minutes?: number }, ctx: Ctx, opts: PlanOpts = {}): Changeset {
  const t = v.get('tasks', taskId);
  if (!t) throw new DomainError('task.not_found');
  if (t.status !== 'open') throw new DomainError('task.not_open');
  if (t.kind === 'appointment') throw new DomainError('plan.appointment_fixed');
  if (v.where('plan_blocks', 'taskId', taskId).some((b) => b.day === at.day && b.status === 'planned')) throw new DomainError('plan.task_planned');
  const s = toMin(normalizeTime(at.start) ?? '');
  if (!Number.isFinite(s)) throw new DomainError('plan.time');
  const len = Math.max(15, Math.min(240, Math.round(at.minutes ?? (Number(t.durationMin) || DEFAULT_TASK_MIN))));
  return savePlanBlock(v, { day: at.day, start: fromMin(s), end: fromMin(s + len), kind: 'task', taskId, title: t.title, activity: activityForTask(t), personId: t.personId ?? null }, ctx, opts);
}
/** move a timed task (not an appointment) to another time of its day; resize with `minutes` */
export function moveTimedTask(v: WorkspaceView, taskId: string, to: { start: string; minutes?: number }, ctx: Ctx, opts: PlanOpts = {}): Changeset {
  const t = v.get('tasks', taskId);
  if (!t) throw new DomainError('task.not_found');
  if (t.kind === 'appointment') throw new DomainError('plan.appointment_fixed');
  if (t.status !== 'open') throw new DomainError('task.not_open');
  if (!t.dueDate) throw new DomainError('plan.day');
  const s = toMin(normalizeTime(to.start) ?? '');
  if (!Number.isFinite(s)) throw new DomainError('plan.time');
  const len = Math.max(5, Math.min(12 * 60, Math.round(to.minutes ?? (Number(t.durationMin) || DEFAULT_TASK_MIN))));
  if (s + len > 24 * 60 - 1) throw new DomainError('plan.after_midnight');
  guardCommitted(v, t.dueDate, fromMin(s), fromMin(s + len), `task:${t.id}`, opts);
  const patch: Partial<Task> = { dueTime: fromMin(s), ...(to.minutes != null || t.durationMin != null ? { durationMin: len } : {}) };
  return { action: 'plan.task_move', entity: 'tasks', entityId: taskId, summary: t.title, puts: [put('tasks', touched(ctx, t, patch))], deletes: [] };
}

/** snap minutes to the grid slot */
export const snap = (min: number, slot: number) => Math.round(min / slot) * slot;

// --- the next free slot (quick add) -------------------------------------------------------------------------------
/** the first free start (aligned to `slot`) with at least `minutes` free, from `notBefore`; null when the day is full */
export function nextFreeStart(v: WorkspaceView, day: Day, minutes: number, opts: { notBefore?: number; slot?: number; items?: PlanItem[] } = {}): number | null {
  const slot = opts.slot ?? plannerSettings(v).slotMin;
  const items = opts.items ?? plannerItems(v, day);
  const f = freeSlots(v, day, items, { notBefore: opts.notBefore, minMinutes: Math.min(minutes, 15) })
    .map((x) => ({ ...x, start: Math.ceil(x.start / slot) * slot }))
    .find((x) => x.end - x.start >= minutes);
  return f ? f.start : null;
}

// --- moving and copying a whole day (owner 22 Oct 2026: «I planned today instead of tomorrow») -----------------------
/** the advisor's own blocks of a day that move with «move the plan» (planned ones; done and cancelled stay as history) */
const ownBlocks = (v: WorkspaceView, day: Day) => v.where('plan_blocks', 'day', day).filter((b) => b.status === 'planned');
export interface PlanMoveCheck {
  from: Day; to: Day;
  /** what moves: own blocks and accepted suggestions */
  blocks: PlanBlock[];
  /** what stays: CRM appointments, planned meetings and timed tasks of the day (never moved from here) */
  staying: PlanItem[];
  /** blocks that would overlap a CRM appointment on the new day (needs a confirmation) */
  overAppointments: string[];
  /** blocks that would overlap something else already planned on the new day (shown, not refused) */
  overOther: string[];
}
export function planMoveCheck(v: WorkspaceView, from: Day, to: Day): PlanMoveCheck {
  const blocks = ownBlocks(v, from);
  const staying = plannerItems(v, from).filter((x) => x.source !== 'block');
  const target = plannerItems(v, to);
  const overAppointments: string[] = [], overOther: string[] = [];
  for (const b of blocks) {
    const r = { start: toMin(b.start), end: toMin(b.end) };
    const hit = target.filter((x) => overlaps(r, interval(x)));
    if (hit.some((x) => x.committed)) overAppointments.push(b.id);
    else if (hit.length) overOther.push(b.id);
  }
  return { from, to, blocks, staying, overAppointments, overOther };
}
/** move the whole plan of `from` to `to`: own blocks and accepted suggestions (same times); CRM appointments, meetings
 *  and timed tasks are never moved. One change, so one «Undo» brings everything back. */
export function movePlan(v: WorkspaceView, from: Day, to: Day, ctx: Ctx, opts: PlanOpts = {}): Changeset {
  if (!isDay(from) || !isDay(to)) throw new DomainError('plan.day');
  if (from === to) throw new DomainError('plan.same_day');
  const c = planMoveCheck(v, from, to);
  if (!c.blocks.length) throw new DomainError('plan.nothing_to_move');
  if (c.overAppointments.length && !opts.allowConflict) throw Object.assign(new DomainError('plan.overlaps_appointment'), { items: c.overAppointments.map((id) => `block:${id}`) });
  return { action: 'plan.move_day', entity: 'plan_blocks', summary: `${from} → ${to} · ${c.blocks.length}`, puts: c.blocks.map((b) => put('plan_blocks', touched(ctx, b, { day: to }))), deletes: [] };
}
export interface PlanCopyPreview { add: PlanBlockInput[]; skipped: number }
/** the blocks a copy (or a template) would add to `day`: anything over an item already planned there is left out */
function fitInto(v: WorkspaceView, day: Day, wanted: PlanBlockInput[]): PlanCopyPreview {
  const taken = plannerItems(v, day).filter((x) => x.status !== 'cancelled').map(interval);
  const add: PlanBlockInput[] = [];
  let skipped = 0;
  for (const w of [...wanted].sort((a, b) => a.start.localeCompare(b.start))) {
    const r = { start: toMin(w.start), end: toMin(w.end) };
    if (!(r.end > r.start) || taken.some((x) => overlaps(r, x))) { skipped++; continue; }
    taken.push(r); add.push(w);
  }
  return { add, skipped };
}
/** «copy from yesterday / from the same day last week»: the blocks of `from` (not task blocks — a task is done once) */
export function planCopyPreview(v: WorkspaceView, from: Day, to: Day): PlanCopyPreview {
  const src = v.where('plan_blocks', 'day', from).filter((b) => b.status !== 'cancelled' && !b.taskId);
  return fitInto(v, to, src.map((b) => ({ day: to, start: b.start, end: b.end, kind: b.kind === 'task' ? 'focus' : b.kind, title: b.title, goalType: b.goalType ?? null, notes: b.notes ?? null, activity: b.activity ?? null, personId: b.personId ?? null })));
}
function addBlocks(v: WorkspaceView, list: PlanBlockInput[], ctx: Ctx, action: string, summary: string): Changeset {
  const puts: Put[] = [];
  for (const b of list) puts.push(...savePlanBlock(v, { ...b, id: newId(), source: 'advisor' }, ctx, { allowConflict: true }).puts);
  return { action, entity: 'plan_blocks', summary, puts, deletes: [] };
}
export function copyPlan(v: WorkspaceView, from: Day, to: Day, ctx: Ctx): Changeset {
  if (!isDay(from) || !isDay(to)) throw new DomainError('plan.day');
  if (from === to) throw new DomainError('plan.same_day');
  const p = planCopyPreview(v, from, to);
  if (!p.add.length) throw new DomainError('plan.nothing_to_copy');
  return addBlocks(v, p.add, ctx, 'plan.copy_day', `${from} → ${to} · ${p.add.length}`);
}
/** the days the planner offers to copy from: yesterday and the same weekday last week */
export const copySources = (day: Day): { yesterday: Day; lastWeek: Day } => ({ yesterday: addDays(day, -1), lastWeek: addDays(day, -7) });

// --- day templates -------------------------------------------------------------------------------------------------
export interface PlanTemplateBlock { activity: PlanActivity; start: string; end: string; title?: string | null }
export interface PlanTemplate { id: string; name: string; blocks: PlanTemplateBlock[]; builtIn?: boolean }
export const PLAN_TEMPLATES_KEY = 'planner.templates';
const TB = (activity: PlanActivity, start: string, end: string): PlanTemplateBlock => ({ activity, start, end });
/** built-in days, written for 09:00 and moved to the start of the office hours (their names are i18n keys) */
export const BUILTIN_TEMPLATES: readonly PlanTemplate[] = [
  { id: 'office', name: 'planner.tpl.office', builtIn: true, blocks: [TB('admin', '09:00', '09:45'), TB('calls', '09:45', '11:15'), TB('followup', '11:15', '12:00'), TB('break', '12:00', '12:15'), TB('prep', '12:15', '13:15'), TB('break', '13:15', '14:00'), TB('calls', '14:00', '15:00'), TB('learning', '15:00', '16:00'), TB('admin', '16:00', '17:00')] },
  { id: 'appointments', name: 'planner.tpl.appointments', builtIn: true, blocks: [TB('prep', '09:00', '09:30'), TB('travel', '09:30', '10:00'), TB('travel', '12:00', '12:30'), TB('break', '12:30', '13:15'), TB('prep', '13:15', '13:45'), TB('travel', '13:45', '14:15'), TB('followup', '16:15', '17:00')] },
  { id: 'calls', name: 'planner.tpl.calls', builtIn: true, blocks: [TB('admin', '09:00', '09:30'), TB('calls', '09:30', '11:00'), TB('break', '11:00', '11:15'), TB('calls', '11:15', '12:45'), TB('break', '12:45', '13:30'), TB('followup', '13:30', '14:30'), TB('calls', '14:30', '16:00'), TB('break', '16:00', '16:15'), TB('followup', '16:15', '17:00')] },
];
function normalizeTemplate(x: unknown): PlanTemplate | null {
  const o = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
  const name = clean(o.name).slice(0, 80), id = clean(o.id);
  if (!name || !id || !Array.isArray(o.blocks)) return null;
  const blocks = (o.blocks as Record<string, unknown>[]).flatMap((b): PlanTemplateBlock[] => {
    const start = normalizeTime(b?.start), end = normalizeTime(b?.end);
    return isPlanActivity(b?.activity) && start && end && toMin(end) > toMin(start) ? [{ activity: b.activity, start, end, title: opt(b.title)?.slice(0, 200) ?? null }] : [];
  });
  return blocks.length ? { id, name, blocks } : null;
}
/** the advisor's own templates (Settings key planner.templates) */
export function ownTemplates(v: WorkspaceView): PlanTemplate[] {
  const raw = getSetting<unknown>(v, PLAN_TEMPLATES_KEY, []);
  return (Array.isArray(raw) ? raw : []).flatMap((x) => { const t = normalizeTemplate(x); return t ? [t] : []; });
}
export const planTemplates = (v: WorkspaceView): PlanTemplate[] => [...BUILTIN_TEMPLATES, ...ownTemplates(v)];
/** the blocks of a template on `day` (built-ins follow the office start; own templates keep their times) */
export function templatePreview(v: WorkspaceView, day: Day, tpl: PlanTemplate, label: (a: PlanActivity) => string): PlanCopyPreview {
  const w = workingWindow(v, day);
  const shift = tpl.builtIn && w ? w.start - 9 * 60 : 0;
  const list: PlanBlockInput[] = [];
  for (const b of tpl.blocks) {
    const s = toMin(b.start) + shift, e = toMin(b.end) + shift;
    if (s < 0 || e > 24 * 60 - 1) continue;
    list.push({ day, start: fromMin(s), end: fromMin(e), activity: b.activity, title: b.title || label(b.activity) });
  }
  return fitInto(v, day, list);
}
export function applyTemplate(v: WorkspaceView, day: Day, tpl: PlanTemplate, label: (a: PlanActivity) => string, ctx: Ctx): Changeset {
  if (!isDay(day)) throw new DomainError('plan.day');
  const p = templatePreview(v, day, tpl, label);
  if (!p.add.length) throw new DomainError('plan.template_nothing');
  return addBlocks(v, p.add, ctx, 'plan.template_apply', `${tpl.id} → ${day} · ${p.add.length}`);
}
/** keep the day's own blocks as a template («save as my template»); task blocks are left out */
export function saveTemplateFromDay(v: WorkspaceView, day: Day, name: string, ctx: Ctx): Changeset {
  const n = clean(name).slice(0, 80);
  if (!n) throw new DomainError('plan.template_name');
  const blocks: PlanTemplateBlock[] = [];
  for (const b of v.where('plan_blocks', 'day', day).filter((x) => x.status !== 'cancelled' && !x.taskId).sort((a, c) => a.start.localeCompare(c.start))) {
    const a = planTypeOf({ source: 'block', kind: b.kind, activity: b.activity, goalType: b.goalType });
    const activity: PlanActivity = isPlanActivity(a) ? a : 'other';
    blocks.push({ activity, start: b.start, end: b.end, title: b.title });
  }
  if (!blocks.length) throw new DomainError('plan.template_empty');
  const list = ownTemplates(v);
  if (list.length >= 20) throw new DomainError('plan.template_limit');
  return { ...saveSetting(v, PLAN_TEMPLATES_KEY, [...list, { id: newId(), name: n, blocks }], ctx), action: 'plan.template_save', summary: n };
}
/** remove one of the advisor's own templates (only on request; the built-ins stay) */
export function deleteTemplate(v: WorkspaceView, id: string, ctx: Ctx): Changeset {
  const list = ownTemplates(v);
  if (!list.some((x) => x.id === id)) throw new DomainError('plan.template_not_found');
  return { ...saveSetting(v, PLAN_TEMPLATES_KEY, list.filter((x) => x.id !== id), ctx), action: 'plan.template_delete', summary: id };
}

// --- relationship meetings: their own count (never appointments) --------------------------------------------------
export interface RelationshipCounts { planned: number; done: number; total: number; coffee: number; referral: number; people: number }
/** relationship meetings (coffee / referral) of the days from…to: planned (still open), done, by type, distinct people */
export function relationshipCounts(v: WorkspaceView, from: Day, to: Day = from): RelationshipCounts {
  const out: RelationshipCounts = { planned: 0, done: 0, total: 0, coffee: 0, referral: 0, people: 0 };
  const people = new Set<string>();
  for (const b of v.rows('plan_blocks')) {
    if (b.day < from || b.day > to || b.status === 'cancelled' || !isSocialActivity(b.activity)) continue;
    out.total++;
    if (b.status === 'done') out.done++; else out.planned++;
    if (b.activity === 'coffee') out.coffee++; else out.referral++;
    if (b.personId) people.add(b.personId);
  }
  out.people = people.size;
  return out;
}
